import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Constants } from '@pwc/pwc-deals-web-common';
import { useHistory } from 'react-router-dom';
import { lowerCase } from 'lodash';
import { useSelector } from 'react-redux';
import { Button, Icon, ProgressSteps } from '@pwc/pwc-deals-ui';
import featureFlags from 'common/helpers/featureFlags';
import { getProjectInitConfig } from '../projectInitSlice';
import { defaultTerritory } from '../../../common/helpers/projectInitUtil';
import { postProject } from '../../../common/services/projectService';
import { refreshTokens } from '../../../common/services/security/userAuthService';
import userTokenService from '../../../common/services/security/userTokenService';
import authorizationService from '../../../common/services/security/authorizationService';
import { FormLayout } from '../../../common/components/layouts';
import MetaDataStep from './steps/MetaDataStep/MetaDataStep';
import PreferenceStep from './steps/PreferenceStep/PreferenceStep';
import TeamMembersStep from './steps/TeamMembersStep/TeamMembersStep';
import SubmitRequestStep from './steps/SubmitRequestStep/SubmitRequestStep';
import RequestError from './steps/RequestError/RequestError';
import RequestSubmittedStep from './steps/RequestSubmittedStep/RequestSubmittedStep';
import './CreateProject.scss';
import {
  financialMagnitudeOptions,
  financialsDecimalCountOptions,
  getDefaultValue,
} from '../common/projectOptions';

const CreateProject = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState(1);
  const projectInitConfig = useSelector(getProjectInitConfig);
  const [stepOneFormValues, setStepOneFormValues] = useState({
    territory:
      projectInitConfig.territoryName || defaultTerritory.territoryName,
    services: [],
  });
  const [stepTwoFormValues, setStepTwoFormValues] = useState({
    showDiagnostic: false,
    currency: Constants.CurrencyOptions.find((currency) => {
      return currency.value === projectInitConfig.defaultCurrency;
    }).value,
    financialsMagnitude: getDefaultValue(financialMagnitudeOptions),
    financialsDecimalCount: getDefaultValue(financialsDecimalCountOptions),
  });
  const [stepThreeFormValues, setStepThreeFormValues] = useState({});
  const [hasError, setHasError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newProjectId, setNewProjectId] = useState(false);

  const steps = [
    { title: 'first', description: 'Project details' },
    { title: 'second', description: 'Preferences' },
    { title: 'third', description: 'Team details' },
    { title: 'four', description: 'Create project' },
  ];

  useEffect(() => {
    if (
      featureFlags.isProjectInitiationFromCDEEnabled &&
      process.env.REACT_APP_TERRITORY !== 'CN'
    )
      history.replace(`/projects/`);
  }, [history]);

  const onBackClick = () => {
    if (currentStep === 1) {
      history.goBack();
    } else {
      setCurrentStep(currentStep - 1);
    }
  };
  const onCancel = () => {
    history.replace(`/projects/`);
  };

  const onStepOneCompleted = async (formValues) => {
    setStepOneFormValues(formValues);
    setCurrentStep(2);
  };

  const onStepTwoCompleted = async (formValues) => {
    setStepTwoFormValues(formValues);
    setCurrentStep(3);
  };

  const onStepThreeCompleted = async (users) => {
    setStepThreeFormValues(users);
    setCurrentStep(4);
  };

  const onStepFourCompleted = async () => {
    const email = await userTokenService.getUserEmail();
    const defaultSelf = {
      email,
      role: Constants.USER_ROLES.TEAM_MEMBER,
      pendingApproval: false,
    };
    const usersWithSelf = stepThreeFormValues.find(
      (u) => lowerCase(u.email) === lowerCase(email)
    )
      ? stepThreeFormValues
      : [...stepThreeFormValues, defaultSelf];

    const project = {
      territory:
        projectInitConfig.territoryCode || defaultTerritory.territoryCode,
      clientName: stepOneFormValues.clientName,
      industryId: stepOneFormValues.industryId,
      name: stepOneFormValues.projectName,
      restriction: parseInt(stepOneFormValues.restriction, 10),
      serviceIds: stepOneFormValues.services.map((sp) => sp.value),
      target: stepOneFormValues.target,
      TargetIndustryId: stepOneFormValues.targetIndustryId,
      description: stepOneFormValues.description,
      type: parseInt(stepOneFormValues.dealType, 10),
      consentLevel: stepOneFormValues.consentLevel,
      IsClientTarget: stepOneFormValues.isClientTarget,
      ShowDiagnostic: stepTwoFormValues.showDiagnostic,
      currency: stepTwoFormValues.currency,
      financialsMagnitude: stepTwoFormValues.financialsMagnitude,
      financialsDecimalCount: stepTwoFormValues.financialsDecimalCount,
      users: usersWithSelf.map((user) => ({
        emailAddress: user.email,
        roleName: user.role,
        pendingApproval: user.pendingApproval,
      })),
    };
    setIsSaving(true);
    setHasError(false);
    try {
      const projectId = await postProject(project);
      const refreshTokenSet = await refreshTokens();
      authorizationService.setTokens(refreshTokenSet);
      setNewProjectId(projectId);
      setCurrentStep(5);
    } catch (e) {
      setCurrentStep(5);
      setHasError(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="project-create">
      <FormLayout headerComponent={<div />}>
        {currentStep !== 4 && (
          <Button
            className="button-menu back-button mb-3"
            type="button"
            onClick={onBackClick}
            data-testid="back-button"
          >
            <Icon name="left-chevron" width={14} className="fill icon-orange" />
            Back
          </Button>
        )}
        {currentStep === 4 ? (
          <h1 data-testid="project-form-header">
            {hasError ? (
              <span>
                {' '}
                <Icon name="alert" width={30} className="fill icon-orange" />
                {t('ProjectError')}
              </span>
            ) : (
              <span>{t('newProjectRequest')}</span>
            )}
          </h1>
        ) : (
          <h1 data-testid="project-form-header">{t('createProject')}</h1>
        )}
        <ProgressSteps current={currentStep} steps={steps} />
        <div className="separator" />
        {currentStep === 1 && (
          <MetaDataStep
            initialValues={stepOneFormValues}
            onCancel={onCancel}
            onStepCompleted={onStepOneCompleted}
          />
        )}{' '}
        {currentStep === 2 && (
          <PreferenceStep
            initialValues={stepTwoFormValues}
            stepOneFormValues={stepOneFormValues}
            onCancel={onCancel}
            onStepCompleted={onStepTwoCompleted}
          />
        )}{' '}
        {currentStep === 3 && (
          <TeamMembersStep
            initialValues={stepThreeFormValues}
            onCancel={onCancel}
            onStepCompleted={onStepThreeCompleted}
            isSaving={isSaving}
            hasError={hasError}
          />
        )}{' '}
        {currentStep === 4 && (
          <SubmitRequestStep
            onCancel={onCancel}
            onStepCompleted={onStepFourCompleted}
            isSaving={isSaving}
          />
        )}{' '}
        {currentStep === 5 && hasError === false && (
          <RequestSubmittedStep
            projectId={newProjectId}
            projectName={stepOneFormValues.projectName}
          />
        )}{' '}
        {currentStep === 5 && hasError && (
          <RequestError onTryAgainClick={onStepFourCompleted} />
        )}
      </FormLayout>
    </div>
  );
};

export default CreateProject;
